import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Clock, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, resolveImg } from "@/lib/api";
import { getRecentSearches, setRecentSearches } from "@/lib/session";

const MAX_RECENT = 5;

// Recent searches are convenience-only and tab-scoped (sessionStorage via
// lib/session.js) - nothing is persisted to localStorage.
function getRecent() { return getRecentSearches(); }
function addRecent(term) {
  const t = (term || "").trim();
  if (!t) return;
  setRecentSearches([t, ...getRecent().filter((r) => r.toLowerCase() !== t.toLowerCase())].slice(0, MAX_RECENT));
}
function removeRecent(term) { setRecentSearches(getRecent().filter((r) => r !== term)); }

/**
 * Polished search box with a live autocomplete panel: matching products, recognised
 * category/jewellery-type/style/occasion shortcuts (via the Smart Search engine's
 * /api/products/suggest), and a "Recent Searches" list (stored client-side) when the box
 * is empty and focused. Used both inline in the header (always visible, every breakpoint)
 * and as the slim sticky re-search bar on the Products (PLP) page — `testidPrefix` keeps
 * each instance's data-testids unique when both are mounted at once.
 */
export const SearchBar = ({
  initialValue = "", placeholder = "Search jewellery...", className = "",
  testidPrefix = "search", autoFocus = false, onSubmitted,
}) => {
  const navigate = useNavigate();
  const [q, setQ] = useState(initialValue);
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({ products: [], shortcuts: [] });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { setQ(initialValue); }, [initialValue]);

  useEffect(() => {
    const onClickOutside = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) { setResults({ products: [], shortcuts: [] }); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/products/suggest", { params: { q: q.trim() } });
        setResults(data);
      } catch { setResults({ products: [], shortcuts: [] }); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const submit = (term) => {
    const value = (term ?? q).trim();
    if (!value) return;
    addRecent(value);
    setOpen(false);
    setQ(value);
    navigate(`/products?search=${encodeURIComponent(value)}`);
    onSubmitted?.(value);
  };

  const goShortcut = (s) => {
    addRecent(q);
    setOpen(false);
    if (s.type === "category") navigate(`/products?category=${encodeURIComponent(s.value)}`);
    else navigate(`/products?search=${encodeURIComponent(s.value)}`);
    onSubmitted?.(s.value);
  };

  const goProduct = (p) => {
    setOpen(false);
    navigate(`/product/${p.id}`);
  };

  const onFocus = () => {
    setOpen(true);
    if (!q.trim()) setRecent(getRecent());
  };

  const showRecent = !q.trim() && recent.length > 0;
  const hasSuggestions = results.products.length > 0 || results.shortcuts.length > 0;
  const showPanel = open && (showRecent || loading || q.trim().length >= 2);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="relative flex items-center">
        <Search className="absolute left-3 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          data-testid={`${testidPrefix}-input`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={onFocus}
          onKeyDown={(e) => { if (e.key === "Escape") { setOpen(false); e.currentTarget.blur(); } }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className="pl-9 pr-8 rounded-full bg-background"
        />
        {q && (
          <button
            type="button"
            data-testid={`${testidPrefix}-clear-button`}
            onClick={() => { setQ(""); setResults({ products: [], shortcuts: [] }); }}
            aria-label="Clear search"
            className="absolute right-3 text-muted-foreground hover:text-foreground transition-colors duration-150"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </form>

      {showPanel && (
        <div
          data-testid={`${testidPrefix}-suggestions-panel`}
          className="absolute left-0 right-0 mt-2 rounded-xl border border-border bg-card/95 backdrop-blur-md shadow-lg z-50 overflow-hidden max-h-[70vh] overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-150"
        >
          {loading && <div className="px-4 py-3 text-sm text-muted-foreground">Searching…</div>}

          {!loading && showRecent && (
            <div className="py-2">
              <div className="px-4 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wide">Recent Searches</div>
              {recent.map((r) => (
                <div key={r} className="flex items-center justify-between px-4 py-2 hover:bg-[hsl(var(--sand))] transition-colors duration-150 group">
                  <button type="button" data-testid={`${testidPrefix}-recent-item`} onClick={() => submit(r)} className="flex items-center gap-2.5 text-sm text-foreground/80 flex-1 text-left min-w-0">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> <span className="truncate">{r}</span>
                  </button>
                  <button
                    type="button"
                    data-testid={`${testidPrefix}-recent-remove`}
                    onClick={() => { removeRecent(r); setRecent(getRecent()); }}
                    aria-label={`Remove ${r} from recent searches`}
                    className="opacity-60 sm:opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity duration-150 shrink-0 ml-2"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {!loading && !showRecent && results.shortcuts.length > 0 && (
            <div className="py-2.5 border-b border-border">
              <div className="px-4 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wide">Quick Shortcuts</div>
              <div className="flex flex-wrap gap-2 px-4 pt-1.5">
                {results.shortcuts.map((s, i) => (
                  <button
                    key={`${s.type}-${s.value}-${i}`}
                    type="button"
                    data-testid={`${testidPrefix}-shortcut-pill`}
                    onClick={() => goShortcut(s)}
                    className="px-3 py-1.5 rounded-full text-xs bg-[hsl(var(--sand))] text-foreground/80 border border-transparent hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors duration-150"
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!loading && !showRecent && results.products.length > 0 && (
            <div className="py-1.5">
              {results.products.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`${testidPrefix}-suggestion-product`}
                  onClick={() => goProduct(p)}
                  className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[hsl(var(--sand))] transition-colors duration-150 text-left"
                >
                  <div className="h-10 w-10 rounded-lg overflow-hidden bg-[hsl(var(--sand))] shrink-0">
                    {p.images?.[0] && <img src={resolveImg(p.images[0])} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-foreground truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground">{p.category}{Number(p.stock) === 0 ? " · Out of stock" : ""}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {!loading && !showRecent && q.trim().length >= 2 && (
            <button
              type="button"
              data-testid={`${testidPrefix}-see-all-button`}
              onClick={() => submit()}
              className="w-full flex items-center justify-between gap-2 px-4 py-3 text-sm font-medium text-[hsl(var(--primary))] hover:bg-[hsl(var(--sand))] transition-colors duration-150 border-t border-border"
            >
              <span className="truncate">{hasSuggestions ? `See all results for "${q}"` : `No quick matches — search all for "${q}"`}</span>
              <ArrowRight className="h-4 w-4 shrink-0" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
